import { ChartOption } from '@customComp/Chart/_config/_interface';
import { formatY } from '@customComp/Chart/_config/_utils';
import { cloneDeep, isObject } from 'lodash-es';
import {
  TMetricItem,
  TSeriesLike,
  TYAxisConfig,
  TYAxisLike,
} from './types';
import { IFieldInfo } from '../types';

/**
 * 设置折线图双Y轴配置
 * @param opt ECharts配置项对象
 * @param chartConfig 图表配置
 * @returns 更新后的配置
 */
export const setDualYAxis = (
  opt: ChartOption,
  chartConfig?: TYAxisConfig,
  config?: { fieldInfos?: IFieldInfo[]; }
): ChartOption => {
  if (!chartConfig?.dualYAxisEnabled) {
    return opt;
  }
  const { yAxis, series } = opt;
  const { fieldInfos } = config || {};
  const baseYAxis: TYAxisLike = Array.isArray(yAxis)
    ? ((yAxis?.[0] as TYAxisLike) || {})
    : ((yAxis as TYAxisLike) || {});

  const seriesData = (Array.isArray(series) ? series : []) as TSeriesLike[];
  const primaryMetrics = Array.isArray(chartConfig?.primaryYAxis?.metrics)
    ? chartConfig.primaryYAxis.metrics
    : [];
  const secondaryMetrics = Array.isArray(chartConfig?.secondYAxis?.metrics)
    ? chartConfig.secondYAxis.metrics
    : [];

  // 通过 metric/tableCode 还原 series key，只有该轴全部指标都是 percent 才使用百分比格式。
  const isAxisPercent = (metrics: TMetricItem[]): boolean =>
    metrics.length > 0 && metrics.every((metricItem) => {
      const metricKey = `${metricItem?.tableCode || 't0'}_${metricItem?.metric || ''}`;
      const fieldInfo = fieldInfos?.find((fieldInfo) => `${fieldInfo?.table_code || 't0'}_${fieldInfo?.origin_name || ''}` === metricKey);
      const seriesItem = seriesData?.find((seriesItem) => seriesItem?.metric === fieldInfo?.name);
      return seriesItem?.valueType === 'percent';
    });
  const isAxisDecimalPercent = (metrics: TMetricItem[]): boolean =>
    metrics.length > 0 && metrics.some((metricItem) => {
      const metricKey = `${metricItem?.tableCode || 't0'}_${metricItem?.metric || ''}`;
      const fieldInfo = fieldInfos?.find((fieldInfo) => `${fieldInfo?.table_code || 't0'}_${fieldInfo?.origin_name || ''}` === metricKey);
      const seriesItem = seriesData?.find((currentSeriesItem) => currentSeriesItem?.metric === fieldInfo?.name);
      if (seriesItem?.valueType !== 'percent') {
        return false;
      }
      const dataList = Array.isArray(seriesItem?.data) ? seriesItem.data : [];
      return dataList.some((dataItem) => Boolean(dataItem?.isDecimalPercent));
    });

  const primaryIsPercent = isAxisPercent(primaryMetrics);
  const secondaryIsPercent = isAxisPercent(secondaryMetrics);
  const primaryIsDecimalPercent = isAxisDecimalPercent(primaryMetrics);
  const secondaryIsDecimalPercent = isAxisDecimalPercent(secondaryMetrics);
  const newYAxis: ChartOption['yAxis'] = [
    {
      ...baseYAxis,
      alignTicks: true,
      position: 'left',
      name: chartConfig?.primaryYAxis?.yAxisName || '',
      nameTextStyle: {
        ...(baseYAxis?.nameTextStyle || {}),
        opacity: 0,
      },
      axisLabel: {
        ...(baseYAxis?.axisLabel || {}),
        formatter(value: number) {
          // 混合指标场景下，percent 序列会在 lineChartHooks 中先 /100 以适配同轴绘制；
          // 这里是 Y 轴展示层，需要乘回 100 后再走百分比格式化。
          const displayValue = primaryIsDecimalPercent && primaryIsPercent ? value * 100 : value;
          return formatY(displayValue, primaryIsPercent);
        },
      },
    },
    {
      ...baseYAxis,
      alignTicks: true,
      position: 'right',
      name: chartConfig?.secondYAxis?.yAxisName || '',
      nameTextStyle: {
        ...(baseYAxis?.nameTextStyle || {}),
        opacity: 0,
      },
      axisLabel: {
        ...(baseYAxis?.axisLabel || {}),
        formatter(value: number) {
          // 混合指标场景下，percent 序列会在 lineChartHooks 中先 /100 以适配同轴绘制；
          // 这里是 Y 轴展示层，需要乘回 100 后再走百分比格式化。
          const displayValue = secondaryIsDecimalPercent && secondaryIsPercent ? value * 100 : value;
          return formatY(displayValue, secondaryIsPercent);
        },
      },
    },
  ];
  return {
    ...opt,
    yAxis: newYAxis,
  };
};

// 图表数据中series.data数组中的元素 不是object的时候返回 {value}
export const getValueObject = (value: any): { value: any; } => (isObject(value) ? (value as { value: any; }) : { value });

// 计算最大值和最小值
export const findMinMax = (dataArray: any[]) => {
  if (dataArray.length === 0) return { maxSum: 0, minSum: 0 };
  const lengths = dataArray.map((item) =>
    (Array.isArray(item?.data) ? item.data.length : 0));
  if (lengths.length === 0) {
    return { maxSum: 0, minSum: 0 };
  }
  // 获取最大长度的 data 数组
  const maxLength = Math.max(...lengths, 0);
  if (maxLength === 0) {
    return { maxSum: 0, minSum: 0 };
  }
  // 初始化一个数组，用来存储每个索引位置的总和
  const sums = new Array(maxLength).fill(0);
  // 遍历每个对象的 data 数组
  dataArray.forEach((item) => {
    const row = Array.isArray(item?.data) ? item.data : [];
    row.forEach((entry: any, index: number) => {
      const dataValue = getValueObject(entry);
      let { value } = dataValue;
      if (typeof value !== 'number') {
        value = parseFloat(value);
        if (isNaN(value)) {
          value = 0; // 如果无法转换为数字，则将其视为0
        }
      }
      sums[index] += value;
    });
  });

  // 计算最大值和最小值
  const maxSum = Math.max(...sums);
  const minSum = Math.min(...sums);
  return { maxSum, minSum };
};

// 找到小数后的第一个非零数
export const firstNonZero = (value: number) => {
  if (value === 0) return 0;
  // 将数值转换为字符串
  const valueStr = value.toString();
  // 找到小数点的位置
  const decimalIndex = valueStr.indexOf('.');
  // 如果没有小数点，直接返回整数部分
  if (decimalIndex === -1) return value;
  // 提取小数部分
  const decimalPart = valueStr.slice(decimalIndex + 1);
  // 找到第一个非零的位置
  const firstNonZeroIndex = decimalPart.search(/[1-9]/);
  if (firstNonZeroIndex === -1) return parseFloat(valueStr);
  // 计算要保留的小数位数
  const decimalPlaces = firstNonZeroIndex + 1;
  // 四舍五入到第一个非零数据后的位数
  const factor = 10 ** decimalPlaces;
  return Math.floor(value * factor) / factor;
};

/**
 * 设置图表 y轴范围(原useChartYaxisRange功能)
 * @param option ECharts配置项对象
 * @param index y轴索引
 * @param type 图表类型（stack时按堆叠计算）
 * @returns 更新后的配置
 */
export const setYaxisRange = ({
  option,
  index = null,
  type = 'other',
}: {
  option: ChartOption;
  index?: string | null;
  type?: string;
}): ChartOption => {
  try {
    const series = cloneDeep(option.series);
    const yAxis = cloneDeep(option.yAxis);
    const seriesList = Array.isArray(series) ? series : [];
    let yAxisList: any[] = [];
    if (Array.isArray(yAxis)) {
      yAxisList = yAxis;
    } else if (yAxis) {
      yAxisList = [yAxis];
    }
    const findMin = (value: { min: number; max: number; }) => {
      let { min } = value;
      let { max } = value;
      // 堆叠图得叠加数据计算
      if (type === 'stack') {
        const { maxSum, minSum } = findMinMax(seriesList);
        max = maxSum;
        min = minSum;
      }
      let showMin;
      if (Math.abs(max - min) < 5) {
        showMin = firstNonZero(min);
      } else {
        showMin = Math.floor(min);
      }
      return showMin;
    };
    let newYAxis;

    if (index && yAxisList[+index]) {
      yAxisList[+index].min = findMin;
      newYAxis = yAxisList;
    } else {
      newYAxis = yAxisList.map((y: any) => ({
        ...y,
        min: findMin,
      }));
    }
    return { ...option, yAxis: newYAxis };
  } catch (e) {
    console.log('setYaxisRange error: ', e);
    return option;
  }
};
